<template>
  <ScrollAreaRoot
    class="overflow-hidden print:overflow-visible!"
    :key="key"
    :class="{ '-mr-3': vertical }"
    ref="scrollArea"
  >
    <ScrollAreaViewport
      class="h-full w-full p-0 focus-visible:outline-none print:min-h-max"
      :class="{
        'pr-3': faRulerVertical,
      }"
    >
      <slot />
    </ScrollAreaViewport>
    <ScrollAreaScrollbar
      v-if="vertical"
      class="bg-neutral/50 hover:bg-neutral group z-20 my-2 flex w-2.5 touch-none rounded-full p-0.5 select-none print:hidden"
      orientation="vertical"
    >
      <ScrollAreaThumb
        class="bg-neutral-content/75 group-hover:bg-neutral-content/75 dark:bg-neutral-content/25 dark:group-hover:bg-neutral-content/50 relative flex-1 rounded-full"
      />
    </ScrollAreaScrollbar>
    <ScrollAreaScrollbar
      v-if="horizontal"
      class="bg-neutral/50 hover:bg-neutral group z-20 mx-2 flex h-2.5 touch-none flex-col rounded-full p-0.5 select-none print:hidden"
      orientation="horizontal"
    >
      <ScrollAreaThumb
        class="bg-neutral-content/75 group-hover:bg-neutral-content/75 dark:bg-neutral-content/25 dark:group-hover:bg-neutral-content/50 relative flex-1 rounded-full"
      />
    </ScrollAreaScrollbar>
  </ScrollAreaRoot>
</template>

<script setup lang="ts">
import { ScrollAreaRoot, ScrollAreaScrollbar, ScrollAreaThumb, ScrollAreaViewport } from 'reka-ui';
import { computed } from 'vue';
import { useTemplateRef } from 'vue';
import { provideScrollParentContext } from '../contexts/scrollParentContext';
import { faRulerVertical } from '@fortawesome/free-solid-svg-icons';

const props = defineProps<{
  vertical?: boolean;
  horizontal?: boolean;
}>();

const scrollAreaRoot = useTemplateRef<InstanceType<typeof ScrollAreaRoot>>('scrollArea');
const scrollParent = computed(() => scrollAreaRoot.value?.viewport);

// We need to invalidate the scroll area when the orientation changes, otherwise the scroll wheel
// listeners will not be registered correctly. This is a workaround for the fact that Reka UI's
// ScrollArea does not react to changes in the orientation props.
const key = computed(() => `${props.vertical ? 'v' : ''}${props.horizontal ? 'h' : ''}`);

provideScrollParentContext(scrollParent);
defineExpose({ scrollAreaRoot });
</script>

<style scoped>
:deep([data-reka-scroll-area-viewport] > div) {
  min-height: 100%;
  display: flex;
  flex-direction: column;
}
</style>
